import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { notificationSettingsAPI } from '../../services/api';

// One place for an asset's email notifications: what is sent, how often, and to whom.

type Cadence = 'instant' | 'daily' | 'weekly' | 'monthly';

interface CategoryInfo {
  key: string;
  label: string;
  cadences: { key: Cadence; schedule: string }[];
  settings: Record<string, boolean>;
}

interface Recipient {
  id: number;
  name: string;
  email: string;
  role?: string;
  enabled: boolean;
}

const CATEGORY_META: Record<string, { icon: string; color: string; desc: string }> = {
  analytics: { icon: 'bi-graph-up', color: '#6366f1', desc: 'Visitors, page views and top pages.' },
  downtime: { icon: 'bi-activity', color: '#ef4444', desc: 'When your site goes down and when it comes back.' },
  security: { icon: 'bi-shield-lock', color: '#f59e0b', desc: 'Vulnerabilities and security scan findings.' },
  seo: { icon: 'bi-search-heart', color: '#10b981', desc: 'SEO score, critical issues and recommendations.' },
};

const CADENCE_LABEL: Record<Cadence, string> = {
  instant: 'Instant alerts',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const Toggle: React.FC<{ checked: boolean; disabled?: boolean; onToggle: () => void; label: string }> = ({ checked, disabled, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onToggle}
    style={{
      width: 44, height: 24, borderRadius: 12, border: 'none', padding: 0, flexShrink: 0,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      background: checked ? '#6366f1' : '#cbd5e1', position: 'relative', transition: 'background 0.2s',
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: checked ? 23 : 3,
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s',
    }} />
  </button>
);

const NotificationSettingsPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;

  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!assetId) { setLoading(false); return; }
    setLoading(true);
    notificationSettingsAPI.get(assetId)
      .then((res) => {
        const d = res.data?.data;
        setCategories(d?.categories || []);
        setRecipients(d?.recipients || []);
        setCanManage(!!d?.can_manage);
        setCurrentUserId(d?.current_user_id ?? null);
      })
      .catch(() => toast.error('Could not load notification settings'))
      .finally(() => setLoading(false));
  }, [assetId]);

  useEffect(() => { load(); }, [load]);

  const toggleCadence = async (cat: CategoryInfo, cadence: Cadence) => {
    if (!assetId) return;
    const key = `${cadence}_enabled`;
    const next = !cat.settings[key];
    const prev = categories;
    setCategories((cs) => cs.map((c) => (c.key === cat.key ? { ...c, settings: { ...c.settings, [key]: next } } : c)));
    setSaving(`${cat.key}.${cadence}`);
    try {
      await notificationSettingsAPI.updateCategory(assetId, cat.key, { [key]: next });
      toast.success(`${cat.label}: ${CADENCE_LABEL[cadence].toLowerCase()} ${next ? 'on' : 'off'}`);
    } catch (e: any) {
      setCategories(prev);
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const toggleRecipient = async (r: Recipient) => {
    if (!assetId) return;
    const next = !r.enabled;
    setRecipients((rs) => rs.map((x) => (x.id === r.id ? { ...x, enabled: next } : x)));
    setSaving(`recipient.${r.id}`);
    try {
      await notificationSettingsAPI.updateRecipient(assetId, r.id, next);
      toast.success(`${r.name || r.email} will ${next ? 'now' : 'no longer'} receive these emails`);
    } catch (e: any) {
      setRecipients((rs) => rs.map((x) => (x.id === r.id ? { ...x, enabled: !next } : x)));
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  if (!assetId) {
    return (
      <DashboardLayout>
        <div className="p-4 text-muted">Select an asset to manage its notifications.</div>
      </DashboardLayout>
    );
  }

  const activeCount = recipients.filter((r) => r.enabled).length;

  return (
    <DashboardLayout>
      <div className="container-fluid py-3" style={{ maxWidth: 860 }}>
        <div className="mb-4">
          <h4 className="fw-bold mb-0">
            <i className="bi bi-bell text-primary me-2"></i>Notification Settings
          </h4>
          <p className="text-muted small mb-0">
            Choose which emails <strong>{currentAsset?.name}</strong> sends, how often, and who receives them.
          </p>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {categories.map((cat) => {
              const meta = CATEGORY_META[cat.key] || { icon: 'bi-bell', color: '#64748b', desc: '' };
              return (
                <div key={cat.key} className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div style={{
                        width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: `${meta.color}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={`bi ${meta.icon}`} style={{ fontSize: 20, color: meta.color }}></i>
                      </div>
                      <div>
                        <div className="fw-semibold">{cat.label}</div>
                        <div className="text-muted small">{meta.desc}</div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {cat.cadences.map((c) => {
                        const on = !!cat.settings[`${c.key}_enabled`];
                        return (
                          <div key={c.key} className="d-flex align-items-center justify-content-between gap-3 py-2 px-3 rounded" style={{ background: '#f8fafc' }}>
                            <div>
                              <div className="small fw-semibold">{CADENCE_LABEL[c.key]}</div>
                              <div className="text-muted" style={{ fontSize: '0.78rem' }}>{c.schedule}</div>
                            </div>
                            <Toggle
                              checked={on}
                              disabled={saving === `${cat.key}.${c.key}`}
                              onToggle={() => toggleCadence(cat, c.key)}
                              label={`${cat.label} ${CADENCE_LABEL[c.key]}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="fw-semibold"><i className="bi bi-people me-2 text-primary"></i>Who receives these emails</div>
                  <span className="badge bg-light text-dark">{activeCount} of {recipients.length} people</span>
                </div>
                <p className="text-muted small mb-3">
                  Everyone in your organisation gets this asset's emails unless switched off here.
                  {!canManage && ' You can switch yourself on or off; ask an owner or admin to change others.'}
                </p>
                <div className="d-flex flex-column">
                  {recipients.map((r) => {
                    const editable = canManage || r.id === currentUserId;
                    return (
                      <div key={r.id} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
                        <div style={{ minWidth: 0 }}>
                          <div className="small fw-semibold text-truncate">
                            {r.name || r.email}
                            {r.id === currentUserId && <span className="text-muted fw-normal"> (you)</span>}
                            {r.role && <span className="badge bg-light text-secondary ms-2 text-capitalize">{r.role}</span>}
                          </div>
                          <div className="text-muted text-truncate" style={{ fontSize: '0.78rem' }}>{r.email}</div>
                        </div>
                        <Toggle
                          checked={r.enabled}
                          disabled={!editable || saving === `recipient.${r.id}`}
                          onToggle={() => toggleRecipient(r)}
                          label={`Emails for ${r.name || r.email}`}
                        />
                      </div>
                    );
                  })}
                  {recipients.length === 0 && <div className="text-muted small">No active users in this organisation.</div>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationSettingsPage;
